import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setTokenState] = useState(() => localStorage.getItem("bf_token"));

  const setToken = (t) => {
    if (t) localStorage.setItem("bf_token", t);
    else localStorage.removeItem("bf_token");
    setTokenState(t);
  };

  const checkAuth = useCallback(async () => {
    try {
      const headers = {};
      const t = localStorage.getItem("bf_token");
      if (t) headers.Authorization = `Bearer ${t}`;
      const r = await axios.get(`${API}/auth/me`, { headers, withCredentials: true });
      setUser(r.data);
    } catch (err) {
      // Only treat a REAL auth failure (401/403) as "logged out". A 5xx,
      // network error, or timeout — the classic pattern when the backend
      // event loop is busy with a long-running LLM generation — must NOT
      // blank the user, or the operator's F5-during-generate refresh
      // bounces them to /login even though their JWT is still valid.
      const status = err?.response?.status;
      const isAuthError = status === 401 || status === 403;
      if (isAuthError) {
        localStorage.removeItem("bf_token");
        setTokenState(null);
        setUser(null);
      } else {
        // Transient error — keep the last-known user and quietly retry
        // once after 2s so the UI recovers as soon as the backend does.
        setTimeout(() => {
          const t2 = localStorage.getItem("bf_token");
          if (!t2) return;
          axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${t2}` },
            withCredentials: true,
          }).then(r2 => setUser(r2.data)).catch(() => { /* keep last-known user */ });
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const r = await axios.post(`${API}/auth/login`, { email, password });
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const signup = async (email, password, name) => {
    const r = await axios.post(`${API}/auth/signup`, { email, password, name });
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
    setToken(null);
    setUser(null);
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const authHeaders = () => {
    const t = localStorage.getItem("bf_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  return (
    <AuthContext.Provider value={{ user, loading, token, login, signup, logout, loginWithGoogle, checkAuth, setUser, authHeaders }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
